import clsx from 'clsx';
import { useMemo } from 'react';

import { ClientOnly } from '@/components/ClientOnly';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';
import { resolveSportIds } from './consts';
import type { ResultsFilters } from './FilterTypes';

export function ResultsChart({
  userId,
  filters,
}: {
  baseUrl: string;
  userId: API.Id | 'me' | 'archive';
}) {
  const isArchive = userId === 'archive';
  const { sportId: sportIdBase, startDate, endDate, playType, free, gameType } = filters;

  const sportId = useMemo(() => resolveSportIds(sportIdBase), [sportIdBase]);

  const serviceIds = useMemo(() => (sportIdBase === 'plus' ? [46, 59] : undefined), [sportIdBase]);

  const getChartRequest = useMemo(() => {
    const req: RequestSchema<
      API.MembershipProductStats & { chart: { date: string; units: string | number }[] } & {
        playType?: Record<API.Results, API.MembershipProductStats>;
      } & {
        period?: Record<API.Results, API.MembershipProductStats>;
      } & {
        gameType?: Record<API.Results, API.MembershipProductStats>;
      }
    > = {
      name: 'getChartRequest',
      input: `/api/v1/messageReceipt/stats/${userId}`,
    };

    return req;
  }, [userId]);

  const chartRequest = useRequest(
    getChartRequest,
    useMemo(
      () => ({
        cache: { maxAge: 90 * 1000 },
        clientOnly: !isArchive, // on /result (my results) we can't fetch this endpoint in SSR as it needs cookies
        query: {
          chart: true,
          count: true,
          tz:
            (typeof Intl !== 'undefined' &&
              Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone) ||
            undefined,
          playType:
            playType === 'full'
              ? ['side', 'total', 'prop', 'future', 'not-available']
              : playType === 'hp'
                ? ['side', 'total', 'not-available']
                : playType === 'st'
                  ? ['side', 'total']
                  : playType === 'pf'
                    ? ['prop', 'future']
                    : playType && typeof playType === 'string'
                      ? [playType]
                      : playType || ['side', 'total', 'prop', 'future', 'not-available'],
          [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
          'message.serviceIds': serviceIds,
          startDate,
          endDate,
          free,
          gameType,
          groupBy: isArchive ? 'playType' : undefined,
        },
      }),
      [isArchive, serviceIds, sportId, playType, startDate, endDate, free, gameType],
    ),
  );

  return (
    <ClientOnly component={() => import('./AreaChart')} request={chartRequest} isArchive={isArchive}>
      <ResultsChartLoading />
    </ClientOnly>
  );
}

export function ResultsChartLoading() {
  return (
    <div
      className={clsx(
        'mt-4 mb-1.5 hidden h-80 w-full min-w-0 sm:inline-block sm:h-72',
        'sm:ml-8 sm:w-[calc(100%-(--spacing(8)))]',
        'animate-pulse',
        'content-center text-center text-zinc-500',
        'bg-[repeating-linear-gradient(to_bottom,transparent_5px,var(--color-tremor-border)_5px,var(--color-tremor-border)_6px,transparent_6px,transparent_68px)] dark:bg-[repeating-linear-gradient(to_bottom,transparent_5px,var(--color-dark-tremor-border)_5px,var(--color-dark-tremor-border)_6px,transparent_6px,transparent_68px)]',
      )}
    >
      Loading chart...
    </div>
  );
}
